import { useCallback, useEffect, useMemo } from "react";
import { useAnalytics } from "../contexts/analytics/hooks";
import { trackErrorEvent } from "../utils/analytics";
import { formatPersonFullName } from "../utils/formatters";
import type { Nurse } from "../utils/models";
import { useLastNameFirst } from "./useNameFormat";
import { useDepartmentRosterQuery } from "./useNurses";

export type RosterLookup = {
	/** Undefined until the roster loads, and after a failure. */
	roster: Nurse[] | undefined;
	/** The roster, with no one in it until it loads — for lists that just show fewer people meanwhile. */
	nurses: Nurse[];
	getNurseById: (id: number) => Nurse | undefined;
	getNurseFormattedName: (nurseId: number) => string;
	isLoading: boolean;
	isError: boolean;
	refetch: () => void;
};

const NO_ONE: Nurse[] = [];
const nursesByIdOfRoster = new WeakMap<Nurse[], Map<number, Nurse>>();
// The query hands every reader the same error object, so this keeps one failed load to one event.
const trackedRosterErrors = new WeakSet<Error>();

const nursesByIdOf = (roster: Nurse[]): Map<number, Nurse> => {
	const cached = nursesByIdOfRoster.get(roster);
	if (cached) return cached;
	const nursesById = new Map(roster.map((nurse) => [nurse.id, nurse]));
	nursesByIdOfRoster.set(roster, nursesById);
	return nursesById;
};

/**
 * The department roster and lookups by id, without HospitalDataProvider. Reads the provider's own roster cache
 * entry, so it adds no request while the provider still loads the roster.
 *
 * Callers of one roster share its id map, so a component can call it per row. A failure is tracked once here but not
 * shown — each caller shows `isError` in its own error state, so a page that reads the roster in two places doesn't
 * stack two messages.
 */
export const useRosterLookup = (departmentId: string): RosterLookup => {
	const lastNameFirst = useLastNameFirst();
	const { trackError } = useAnalytics();
	const { data: roster, error, isLoading, isError, refetch: refetchRoster } = useDepartmentRosterQuery(departmentId);

	useEffect(() => {
		if (!error || trackedRosterErrors.has(error)) return;
		trackedRosterErrors.add(error);
		trackError("department_roster_fetch_error", { department_id: departmentId });
	}, [error, departmentId, trackError]);

	// DEFAULT: no one until the roster loads; pages wait on `isLoading` before building anything that needs everyone.
	const nurses = roster ?? NO_ONE;
	const nursesById = nursesByIdOf(nurses);

	const getNurseById = useCallback((id: number): Nurse | undefined => nursesById.get(id), [nursesById]);

	const getNurseFormattedName = useCallback(
		(nurseId: number): string => {
			const nurse = nursesById.get(nurseId);
			if (!nurse) {
				// Before the roster loads every id is missing; only an id the loaded roster lacks is a bug.
				if (roster) trackErrorEvent("invalid_nurse_id", `getNurseFormattedName: Nurse not found for id ${nurseId}`, { nurse_id: nurseId });
				return "";
			}
			return formatPersonFullName(nurse, lastNameFirst);
		},
		[nursesById, roster, lastNameFirst],
	);

	const refetch = useCallback(() => void refetchRoster(), [refetchRoster]);

	return { roster, nurses, getNurseById, getNurseFormattedName, isLoading, isError, refetch };
};

/** Whether a screen built on the staff list can show: what `RosterLoadFallback` reads. */
export type RosterLoadState = Pick<RosterLookup, "isLoading" | "isError">;

/**
 * The staff list's load state for a screen that waits for it: loading until it first arrives, and failed only when it
 * never did. A failed background refetch keeps the loaded list, so it doesn't take the screen away.
 */
export const useRosterLoadState = (departmentId: string): RosterLoadState => {
	const { roster, isLoading, isError } = useRosterLookup(departmentId);
	const hasRoster = roster !== undefined;
	return useMemo(() => ({ isLoading, isError: isError && !hasRoster }), [isLoading, isError, hasRoster]);
};
